import { ASSIGNED_SEATING } from "@shared/constants";
import Konva from "konva";
import SeatingService from "../services/SeatingService";
import { PublicBaseSegmentSetSerializer } from "../types/seating-types";
import SeatingUtilsFactory from "./SeatingUtilsFactory";

type SegmentReturnType = {
  node: Konva.Group;
  label: Konva.Text;
  hideLabel: () => void;
  showLabel: () => void;
};

export default class SegmentFactory {
  public seatingService: SeatingService;

  constructor(seatingService: SeatingService) {
    this.seatingService = seatingService;
  }

  create(segmentData: PublicBaseSegmentSetSerializer): SegmentReturnType {
    const { rotation } = segmentData.transform;

    const group = new Konva.Group({
      x: segmentData.transform.x,
      y: segmentData.transform.y,
      draggable: false,
      id: segmentData.identifier,
    });

    // In some cases filter out segments by the permissions
    // so we don't display all segments as active
    // e.g. group sales, dynamic holds
    // If segments are not filtered out by permissions show
    // all as active
    // (TODO: remove first condition when segment_id is added to the public map permissions)
    const isActive =
      this.seatingService.isAssignerMap() ||
      !this.seatingService.segmentsWithPermissions?.size ||
      this.seatingService.segmentsWithPermissions.has(segmentData.id);

    // Display segment as sold_out if it is in the sold_out_segments array
    const isSoldOut = this.seatingService.soldOutSegments.includes(
      segmentData.id
    );

    let segmentStyle = ASSIGNED_SEATING.SEGMENT.STYLE.UNAVAILABLE;

    if (isActive) {
      segmentStyle = isSoldOut
        ? ASSIGNED_SEATING.SEGMENT.STYLE.SOLD_OUT
        : ASSIGNED_SEATING.SEGMENT.STYLE.AVAILABLE;
    }

    const { colorFill } = segmentStyle;

    const lines = new Konva.Line({
      points: segmentData.display.points.flatMap((point: number[]) => point),
      fill: colorFill,
      closed: true,
      perfectDrawEnabled: false,
      name: ASSIGNED_SEATING.SEGMENT.ID,
      isActive: isActive && !isSoldOut,
      data: segmentData,
    });

    const label = SegmentFactory.createLabel(segmentData.identifier, {
      ...SeatingUtilsFactory.getPolygonCentroid(segmentData.display.points),
      hidden: false,
    });
    // counter spin labels so they are always aligned normally
    SeatingUtilsFactory.rotateAroundCenter(label, -(rotation ?? 0));

    group.add(lines);
    group.add(label);

    SeatingUtilsFactory.rotateAroundCenter(group, rotation ?? 0);
    return {
      node: group,
      label,
      hideLabel: () => {
        label.hide();
      },
      showLabel: () => {
        label.show();
      },
    };
  }

  static createLabel = (text: string, params: Konva.TextConfig): Konva.Text => {
    const { x, y, hidden } = params;
    const fontSize = 30;

    const label = new Konva.Text({
      x,
      y,
      text: text.toUpperCase(),
      fontSize,
      fill: "#fff",
      listening: false,
    });

    // set X and Y values on the label
    label.x(label.x() - label.width() / 2);
    label.y(label.y() - label.height() / 2);

    if (hidden) {
      label.hide();
    }

    return label;
  };

  // eslint-disable-next-line class-methods-use-this
  async handleSegmentClick(segmentId: number | null): Promise<void> {
    const cb = SeatingService.getSegmentSelectCallback();
    cb(segmentId);
  }
}
